import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Handles the Emergent Google Auth callback:
 *   /auth/callback#session_id=...
 * Exchanges the session_id via our backend, then navigates to /account/settings.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { applyUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const search = window.location.search || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : (search.startsWith("?") ? search.slice(1) : ""));
    const session_id = params.get("session_id");
    if (!session_id) {
      toast.error("Missing session_id in callback URL.");
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/emergent/session", { session_id });
        applyUser(data.user, data.session_token);
        toast.success(`Welcome, ${data.user.display_name}!`);
        // Clean URL
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/account/settings", { replace: true, state: { user: data.user } });
      } catch (err) {
        toast.error(err?.response?.data?.error || "Google sign-in failed");
        navigate("/login", { replace: true });
      }
    })();
  }, [applyUser, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <div className="font-display text-xl text-[#071b22]" data-testid="auth-callback-loading">Finishing sign-in…</div>
        <div className="text-sm text-[#252A34]/60 mt-2">Just a moment.</div>
      </div>
    </div>
  );
}
